import { Immutable } from 'nuclear-js';
import CommonValues from 'resources/common-values';
import moment from 'moment';

export function seasonFromTermNumber(termNumber) {
    if (!termNumber) {
        throw new Error('termNumber must be populated');
    }

    let seasonNumber = termNumber.toString().slice(4);
    let season;

    if (seasonNumber == '01') {
        season = 'Winter';
    } else if (seasonNumber == '02') {
        season = 'Spring';
    } else if (seasonNumber == '03') {
        season = 'Summer';
    } else if (seasonNumber == '04') {
        season = 'Fall';
    } else {
        return null;
    }

    return season;
}

export function yearFromTermNumber(termNumber, fullYear = false) {
    if (!termNumber) {
        throw new Error('termNumber must be populated');
    }

    return fullYear ? termNumber.toString().slice(0, 4) : termNumber.toString().slice(2, 4);
}

export function textFromTermNumber(termNumber, fullYear = false, isAbbreviated = false) {
    if (!termNumber) {
        throw new Error('termNumber must be populated');
    }

    let year = yearFromTermNumber(termNumber, fullYear);
    if (!fullYear && isAbbreviated) {
        year = "'" + year;
    }
    const season = seasonFromTermNumber(termNumber);

    return season && year && `${season} ${year}`;
}

// Separate function from textFromTermNumber required for different desired results
// Ex: textFromTermNumber(201604, true) = Fall 2016
// Ex: fullTextFromTermNumber(201604) = Fall 2016-17

export function fullTextFromTermNumber(termNumber) {
    let year = termNumber;
    if (termNumber % 10 === 4) {
        year = Math.floor(termNumber / 100) + 1;
    }
    year = (parseInt(year.toString().slice(0, 4)) - 1)
        .toString()
        .concat('-')
        .concat(year.toString().slice(2, 4));

    const season = seasonFromTermNumber(termNumber);

    return season && year && `${season} ${year}`;
}

export function findAcademicYearForTermNumber(termNumber) {
    // group terms fall-summer together.  Add 1 to AY for fall, but take AY as is for all other terms.
    //      For example:
    //         201504 => 2016
    //         201601 => 2016
    //         201602 => 2016
    //         201603 => 2016
    //         201604 => 2017
    if (termNumber % 10 === 4) {
        return Math.floor(termNumber / 100) + 1;
    }
    return Math.floor(termNumber / 100);
}

export const findTermIdsFromTestEvents = (testEvents) => {
    if (!testEvents) return null;
    let termsWithTestEvents = Immutable.List().asMutable();
    testEvents.forEach((v) => {
        if (termsWithTestEvents.indexOf(v.get('termNumber')) === -1) {
            termsWithTestEvents.push(v.get('termNumber'));
        }
    });

    return termsWithTestEvents
        .sort((t1, t2) => {
            return t2 - t1;
        })
        .toList();
};

export function mostRecentTerm() {
    let mostRecentTerm = new Immutable.Map([
        ['name', 'Most Recent'],
        ['termNumber', CommonValues.mostRecentTermNumber],
    ]);
    return mostRecentTerm;
}

export function findCurrentTerm(terms) {
    if (!terms) {
        return null;
    }

    /* If you want to mock out what the current term is, add the following entry to your .env
       MOCK_CURRENT_TERM=202104
       It will return that term IF that term is available in the partnerTerms supplied to this function
     */

    if (process.env.MOCK_CURRENT_TERM) {
        const mockCurrentTerm = parseInt(process.env.MOCK_CURRENT_TERM);
        if (mockCurrentTerm) {
            return terms.find((t) => {
                return t.get('termNumber') === parseInt(mockCurrentTerm);
            });
        }
    }

    const currentDate = moment.utc().valueOf();
    return terms.find(
        (t) => {
            // adjust end day of term so that the last day is handled properly
            const modifiedEndDate = moment(t.get('endDate'))
                .utc()
                .add(1, 'days')
                .valueOf();
            return t.get('startDate') < currentDate && currentDate < modifiedEndDate;
        },
        null,
        null
    );
}

export function futureTermsSetup(currentTerm, terms) {
    if (!currentTerm || !terms) {
        return false;
    }

    let termsWithoutSummer = terms.filter(
        (t) => {
            return t.get('termNumber') % 100 !== 3;
        },
        null,
        null
    );

    //look for future terms 1 year out from current term.
    let futureTerms = termsWithoutSummer.filter(
        (t) => {
            return (
                t.get('termNumber') > currentTerm.get('termNumber') &&
                t.get('termNumber') <= currentTerm.get('termNumber') + 100
            );
        },
        null,
        null
    );

    //if we have all 3 future terms set up 1 year from the current term, we don't have any missing terms.
    return futureTerms.size === 3 ? true : false;
}

export function getSelectedTermBySubject(mostRecentTestEvents, selectedTermNumber, subject) {
    let termInfo = new Map();

    const isMostRecent = selectedTermNumber == CommonValues.mostRecentTermNumber;

    if (isMostRecent && mostRecentTestEvents && mostRecentTestEvents.get(subject)) {
        selectedTermNumber = mostRecentTestEvents.getIn([subject, 'termNumber']);
    }

    termInfo.set('selectedTermNumber', selectedTermNumber).set('isMostRecent', isMostRecent);

    return termInfo;
}

export default {
    textFromTermNumber,
    fullTextFromTermNumber,
    findAcademicYearForTermNumber,
    findTermIdsFromTestEvents,
    mostRecentTerm,
    findCurrentTerm,
    futureTermsSetup,
    getSelectedTermBySubject,
    seasonFromTermNumber,
    yearFromTermNumber,
};
